import React, { useState, useEffect, useCallback } from 'react';
import styles from './ConfigSettings.module.css';
import { GET, PUT } from '../../../../config/api';

const emptyForm = {
  show_social_login: false,
  maintenance_mode: false,
  show_modal: false,
  modal_json: {
    backgroundImage: '',
    title: '',
  },
  ios: '',
  android: '',
};

const formatVersionList = (value) => {
  if (Array.isArray(value)) {
    return value.join('\n');
  }
  if (typeof value === 'string') {
    return value;
  }
  return '';
};

const ConfigSettings = () => {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  const applyConfigToForm = (configData) => ({
    show_social_login: !!configData?.show_social_login,
    maintenance_mode: !!configData?.maintenance_mode,
    show_modal: !!configData?.show_modal,
    modal_json: {
      backgroundImage: configData?.modal_json?.backgroundImage || '',
      title: configData?.modal_json?.title || '',
    },
    ios: formatVersionList(configData?.ios),
    android: formatVersionList(configData?.android),
  });

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, message } = await GET('website/admin/config');
    if (data?.config) {
      setConfig(data.config);
      setForm(applyConfigToForm(data.config));
    } else {
      setConfig(null);
      setForm(emptyForm);
      setError(message || 'Failed to load config');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const buildPayload = () => ({
    show_social_login: form.show_social_login,
    maintenance_mode: form.maintenance_mode,
    show_modal: form.show_modal,
    modal_json: {
      backgroundImage: form.modal_json.backgroundImage.trim(),
      title: form.modal_json.title.trim(),
    },
    ios: form.ios
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean),
    android: form.android
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean),
  });

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage(null);

    const { data, message } = await PUT('website/admin/config', {}, buildPayload());

    if (data?.config) {
      setConfig(data.config);
      setForm(applyConfigToForm(data.config));
      setSaveMessage({ type: 'success', text: 'Config updated. App cache refreshed.' });
    } else {
      setSaveMessage({ type: 'error', text: message || 'Failed to update config.' });
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
        <div className={styles['spinner-text']}>Loading config...</div>
      </div>
    );
  }

  if (error && !config) {
    return <div className={styles.error}>Error: {error}</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.hero}>
        <div className={styles.brand}>
          <img src="/logo192.png" alt="Tellmiz logo" className={styles['brand-logo']} />
          <div>
            <p className={styles.eyebrow}>Tellmiz Admin</p>
            <h1>App Config</h1>
            <p className={styles.subhead}>Manage feature flags, maintenance mode, and app versions.</p>
          </div>
        </div>
        <div className={styles['stat-row']}>
          <div className={styles['stat-card']}>
            <span>Maintenance</span>
            <strong>{form.maintenance_mode ? 'On' : 'Off'}</strong>
          </div>
          <div className={styles['stat-card']}>
            <span>Social Login</span>
            <strong>{form.show_social_login ? 'On' : 'Off'}</strong>
          </div>
          <div className={styles['stat-card']}>
            <span>Home Modal</span>
            <strong>{form.show_modal ? 'On' : 'Off'}</strong>
          </div>
        </div>
      </div>

      {saveMessage && (
        <div className={`${styles.banner} ${styles[saveMessage.type]}`}>
          {saveMessage.text}
        </div>
      )}

      <div className={styles.toolbar}>
        <button className={styles['refresh-btn']} onClick={fetchConfig} disabled={loading || saving}>
          Refresh
        </button>
        <button className={styles['save-btn']} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      </div>

      <div className={styles['config-panel']}>
        <section className={styles.section}>
          <h2 className={styles['section-title']}>Feature flags</h2>
          <div className={styles['edit-form']}>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={form.show_social_login}
                onChange={(e) => setForm({ ...form, show_social_login: e.target.checked })}
              />
              Show social login
            </label>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={form.maintenance_mode}
                onChange={(e) => setForm({ ...form, maintenance_mode: e.target.checked })}
              />
              Maintenance mode
            </label>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={form.show_modal}
                onChange={(e) => setForm({ ...form, show_modal: e.target.checked })}
              />
              Show home modal
            </label>
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles['section-title']}>Home modal</h2>
          <div className={styles['edit-form']}>
            <label>
              Background image URL
              <input
                type="text"
                value={form.modal_json.backgroundImage}
                onChange={(e) =>
                  setForm({
                    ...form,
                    modal_json: { ...form.modal_json, backgroundImage: e.target.value },
                  })
                }
                placeholder="https://..."
              />
            </label>
            <label>
              Title
              <input
                type="text"
                value={form.modal_json.title}
                onChange={(e) =>
                  setForm({
                    ...form,
                    modal_json: { ...form.modal_json, title: e.target.value },
                  })
                }
                placeholder="e.g. Fall 2024"
              />
            </label>
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles['section-title']}>Allowed app versions</h2>
          <div className={styles['edit-form']}>
            <label>
              iOS versions (one per line)
              <span className={styles['field-hint']}>
                Users on versions not listed here will be prompted to update.
              </span>
              <textarea
                rows={4}
                value={form.ios}
                onChange={(e) => setForm({ ...form, ios: e.target.value })}
                placeholder={'5.1.21-1\n5.1.20-1'}
              />
            </label>
            <label>
              Android versions (one per line)
              <span className={styles['field-hint']}>
                Users on versions not listed here will be prompted to update.
              </span>
              <textarea
                rows={4}
                value={form.android}
                onChange={(e) => setForm({ ...form, android: e.target.value })}
                placeholder={'5.1.21-27\n5.1.20-27'}
              />
            </label>
          </div>
        </section>

        {config && (
          <div className={styles.meta}>
            <span><strong>ID:</strong> {config._id}</span>
            <span><strong>Updated:</strong> {config.updatedAt ? new Date(config.updatedAt).toLocaleString() : '—'}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfigSettings;
